import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import ButtonV2 from "../../../../components/ui/button/v2";
import { AreaOfFraud } from "../../../../api/cases";
import { Account } from "../../../../api/accounts";
import { SectionIssue } from "./derive";
import { SectionAccordion, CollectionModal, ViewMoreButton } from "./SectionShell";
import AccountPartyRow, { AddAccountPartyRow } from "./AccountPartyRow";

export const INLINE_ROW_LIMIT = 2;
export interface AccountPartiesSectionProps {
  parties: AddAccountPartyRow[];
  onPartiesChange: (next: AddAccountPartyRow[]) => void;
  accounts: Account[];
  area_of_fraud: AreaOfFraud;
  showErrors: boolean;
  issue: SectionIssue;
  focus: { key: string; seq: number };
}

const buildEmptyPartyRow = (): AddAccountPartyRow => {
  return {
    stakeholder: {
      stakeholder_id: uuidv4(),
      name: "",
      is_potential_perpetrator: true,
    },
    relationshipGroups: [{id: uuidv4(), relatedAs: undefined, accountNumbers: []}],
  };
};

const AccountPartiesSection: React.FC<AccountPartiesSectionProps> = ({
  parties,
  onPartiesChange,
  accounts,
  area_of_fraud,
  showErrors,
  issue,
  focus,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const addEnabled = issue.incompleteRows === 0;

  useEffect(() => {
    if (focus.key !== "primaryParties") return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (issue.hiddenInvalid > 0) setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus.seq]);

  const add = () => {
    const next = [...parties, buildEmptyPartyRow()];
    onPartiesChange(next);
    if (next.length > INLINE_ROW_LIMIT) setModalOpen(true);
  };

  const onPartyChange = (updated: AddAccountPartyRow) => {
    onPartiesChange(
      parties.map((p) =>
        p.stakeholder.stakeholder_id === updated.stakeholder.stakeholder_id ? updated : p
      )
    );
  };

  const remove = (partyId: string) => {
    onPartiesChange(parties.filter((p) => p.stakeholder.stakeholder_id !== partyId));
  };

  const renderRows = (list: AddAccountPartyRow[]) => (
    <div className="flex flex-col gap-4">
      {list.map((party) => (
        <AccountPartyRow
          key={party.stakeholder.stakeholder_id}
          party={party}
          allAdded={parties}
          accounts={accounts}
          area_of_fraud={area_of_fraud}
          showErrors={showErrors}
          onChange={onPartyChange}
          onDelete={() => remove(party.stakeholder.stakeholder_id)}
        />
      ))}
    </div>
  );

  const addButton = (
    <ButtonV2
      variant="ghost"
      size="sm"
      disabled={!addEnabled}
      onClick={add}
      className="text-[#3551F3] bg-transparent self-start disabled:opacity-40"
      prefix={<Plus width="16" height="16" />}
    >
      Add Primary Party
    </ButtonV2>
  );

  return (
    <>
      <div ref={rootRef}>
        <SectionAccordion
          title="Primary Parties"
          count={parties.length}
          invalidCount={showErrors ? issue.incompleteRows : 0}
          onExpand={() => setModalOpen(true)}
        >
          <div className="flex flex-col gap-4">
            {renderRows(parties.slice(0, INLINE_ROW_LIMIT))}
            <div className="flex items-center justify-between">
              {addButton}
              <ViewMoreButton
                overflowCount={parties.length - INLINE_ROW_LIMIT}
                singular="party"
                plural="parties"
                invalidCount={showErrors ? issue.hiddenInvalid : 0}
                onClick={() => setModalOpen(true)}
              />
            </div>
          </div>
        </SectionAccordion>
      </div>

      {modalOpen && (
        <CollectionModal
          title="Primary Parties"
          onClose={() => setModalOpen(false)}
          onDone={() => setModalOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {renderRows(parties)}
            {addButton}
          </div>
        </CollectionModal>
      )}
    </>
  );
};

export default AccountPartiesSection;
